// Confirmation sheet shown when the user taps End in the Live Activity /
// Dynamic Island. The AppIntent opens Roava and queues a `confirmEndTrip`
// command; this sheet surfaces the actual confirmation so an accidental
// Lock Screen tap can NEVER silently stop tracking.

import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CONFIRM_END_TRIP_EVENT,
  cancelPendingEndTrip,
  executeConfirmedEndTrip,
  hasPendingEndConfirm,
} from "@/lib/live-activity/commands";

export function ConfirmEndTripSheet() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Cold-launch check: if the AppIntent fired while the app was killed,
    // the pending flag persists in kv. Show the sheet on first mount.
    void hasPendingEndConfirm().then((pending) => {
      if (!cancelled && pending) setOpen(true);
    });
    const onConfirm = () => setOpen(true);
    window.addEventListener(CONFIRM_END_TRIP_EVENT, onConfirm);
    // Re-check on foreground in case a tap happened during suspension.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      void hasPendingEndConfirm().then((pending) => {
        if (pending) setOpen(true);
      });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener(CONFIRM_END_TRIP_EVENT, onConfirm);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const handleCancel = async () => {
    setBusy(true);
    try { await cancelPendingEndTrip(); } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const handleConfirm = async () => {
    setBusy(true);
    try { await executeConfirmedEndTrip(); } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        // Treat dismissal (tap outside, Esc, swipe) as Cancel — never as a
        // silent confirm. Tracking must remain active until the user taps
        // "End tracking" explicitly.
        if (!next) void handleCancel();
        else setOpen(true);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>End tracking for this trip?</AlertDialogTitle>
          <AlertDialogDescription>
            Roava will stop recording your route and dismiss the Live Activity.
            You can keep tracking by tapping Cancel.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={busy}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            End tracking
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
